import React, {useEffect, useState} from 'react'
import api from '../../api/axios'
import {Box, Button, Step, StepLabel, Stepper, TextField,} from '@mui/material'
import {useLocation, useNavigate} from 'react-router-dom'
import {useSelector} from 'react-redux';
import {RootState} from '../../store';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import Lottie from 'lottie-react';
import loadingAnimation from '../../assets/lottie/loading.json';

const VerifyAndCompleteProfile = () => {
  const {t} = useTranslation();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  const steps = [t('verifyAccount.personalData'), t('verifyAccount.NewStep2'), t('verifyAccount.confirmation')]

  const location = useLocation()
  const navigate = useNavigate()

  const [activeStep, setActiveStep] = useState(0)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    street1: '',
    street2: '',
    postalCode: '',
    city: '',
    nationality: '',
    phone: ''
  })

  const [touchedFields, setTouchedFields] = useState<{ [key: string]: boolean }>({})

  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})

  const [isStepValid, setIsStepValid] = useState(false)

  const searchParams = new URLSearchParams(location.search)
  const token = searchParams.get('token')

  useEffect(() => {
    if (isAuthenticated === true) {
      toast.error(t('verifyAccount.loggedIn'))
      navigate('/')
    }
  }, [isAuthenticated, navigate, t]);

  useEffect(() => {
    if (!token) {
      setLoading(false)
      toast.error(t('verifyAccount.tokenNotAvailable'))
      navigate('/')
      return
    }

    api.post('/user/account-verification', null, {
      params: {token}
    })
    .then(() => {
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]))
        setFormData(prev => ({...prev, email: decoded.sub}))
      } catch {
        toast.error(t('verifyAccount.tokenNotValid'))
        navigate('/')
      }
    })
    .catch(() => {
      toast.error(t('verifyAccount.tokenVerificationFailed'))
      navigate('/')
    })
    .finally(() => setLoading(false))
  }, [location.search])

  const validateStep = (step: number, data: typeof formData) => {
    const errors: { [key: string]: string } = {}

    if (step === 0) {
      if (!data.firstName.trim()) errors.firstName = t('verifyAccount.firstName') + ' ' + t('verifyAccount.required')
      if (!data.lastName.trim()) errors.lastName = t('verifyAccount.lastName') + ' ' + t('verifyAccount.required')
      if (!data.street1.trim()) errors.street = t('verifyAccount.street1') + ' ' + t('verifyAccount.required')
      if (!data.street2.trim()) errors.street = t('verifyAccount.street2') + ' ' + t('verifyAccount.required')
      if (!data.postalCode.trim()) errors.postalCode = t('verifyAccount.postalCode') + ' ' + t('verifyAccount.required')
      if (!data.city.trim()) errors.city = t('verifyAccount.city') + ' ' + t('verifyAccount.required')
      if (!data.nationality.trim()) errors.nationality = t('verifyAccount.nationality') + ' ' + t('verifyAccount.required')
      if (!data.phone.trim()) errors.phone = t('verifyAccount.phone') + ' ' + t('verifyAccount.required')
    } else if (step === 1) {
    }

    return errors
  }

  useEffect(() => {
    const errors = validateStep(activeStep, formData)
    setFieldErrors(errors)
    setIsStepValid(Object.keys(errors).length === 0)
  }, [formData, activeStep])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target
    setFormData(prev => ({...prev, [name]: value}))
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const {name} = e.target
    setTouchedFields(prev => ({...prev, [name]: true}))
  }

  const handleNext = () => {
    const errors = validateStep(activeStep, formData)
    setFieldErrors(errors)

    const stepFields = getFieldsForStep(activeStep)
    const newTouched = {...touchedFields}
    stepFields.forEach(field => {
      newTouched[field] = true
    })
    setTouchedFields(newTouched)

    if (Object.keys(errors).length === 0) {
      setActiveStep(prev => prev + 1)
    }
  }

  const handleBack = () => {
    setActiveStep(prev => prev - 1)
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      // Erst speichern, dann einloggen – aber nur bei Erfolg!
      await toast.promise(
          api.post('/user/profile-completion', formData, {
            params: {token}
          }),
          {
            pending: t('toastify.pending'),
            success: t('verifyAccount.saveProfileSuccess'),
            error: t('verifyAccount.saveProfileFailed')
          }
      )

      // Nur wenn das klappt, geht's hier weiter:
      await toast.promise(
          api.post('/sign-in', {
            email: formData.email,
          }),
          {
            pending: t('toastify.pending'),
            success: t('signIn.success'),
            error: t('signIn.invalid')
          }
      )

      navigate('/')
    } catch {
    } finally {
      setSubmitting(false)
    }
  }


  const getFieldsForStep = (step: number): string[] => {
    if (step === 0) return ['firstName', 'lastName', 'street1', 'street2', 'postalCode', 'city', 'nationality', 'phone']
    return []
  }

  if (loading) {
    return (
        <div className="flex items-center justify-center h-screen bg-white dark:bg-black">
          <Lottie animationData={loadingAnimation} loop className="w-64"/>
        </div>
    );
  }

  return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-light dark:bg-dark px-4">
        <Box sx={{width: '100%', maxWidth: 600, mt: 4, mx: 'auto'}}>
          <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                justifyContent: 'center',
                '.MuiStep-root': {padding: 0, minWidth: 'auto'},
                '.MuiStepLabel-label': {
                  marginTop: 2,
                  whiteSpace: 'nowrap',
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                },
                '.MuiStepIcon-root': {
                  fontSize: 28
                }
              }}
          >
            {steps.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
            ))}
          </Stepper>
        </Box>

        {activeStep === 0 && (
            <Box sx={{mt: 4, display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 600, mx: 'auto'}}>
              {['firstName', 'lastName', 'street1', 'street2', 'postalCode', 'city', 'nationality', 'phone'].map(field => (
                  <TextField
                      key={field}
                      label={t(`verifyAccount.${field}`)}
                      name={field}
                      value={formData[field as keyof typeof formData]}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={!!fieldErrors[field] && touchedFields[field]}
                      helperText={touchedFields[field] ? fieldErrors[field] : ''}
                  />
              ))}
            </Box>
        )}

        {activeStep === 1 && (
            <Box sx={{mt: 4, width: '100%', maxWidth: 400, mx: 'auto'}}>
              Password Setup removed
            </Box>
        )}


        <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 4, width: '100%', maxWidth: 600, mx: 'auto'}}>
          <Button disabled={activeStep === 0} onClick={handleBack}>{t('verifyAccount.back')}</Button>
          {activeStep < steps.length - 1 ? (
              <Button variant="contained" onClick={handleNext} disabled={!isStepValid}>{t('verifyAccount.next')}</Button>
          ) : (
              <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                {t('verifyAccount.submit')}
              </Button>
          )}
        </Box>
      </div>
  )
}

export default VerifyAndCompleteProfile
